// components/entities/pages/UnitPage.tsx
import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  LEASING_ACTION_TYPES,
  LeasingActionType,
  UnitInventoryEntityState,
  UnitRuntimeState,
} from '#types/types';
import { EntityPageTabs } from '@/components/portal/EntityPageTabs';
import { useFloorManagement } from '@/components/portal/FloorManagement';
import { BrandMark } from '@/components/ui/BrandMark';
import { LeadsTable } from '@/components/entities/tables/leads/LeadsTable';
import { DealsTable } from '@/components/entities/tables/deals/DealsTable';
export type UnitWorkspaceTab = 'OVERVIEW' | 'LEADS' | 'DEALS';

const resolveUnitWorkspaceTabForAction =(actionType?: LeasingActionType): UnitWorkspaceTab => {
  // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
  // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (actionType) {
    case 'REVIEW_CANDIDATES':
      return 'LEADS';
    case 'DECIDE_BASE_RENT':
    case 'DECIDE_LEASE_DURATION':
    case 'DECIDE_RENT_FREE_PERIOD':
    case 'DECIDE_FITOUT_CONTRIBUTION':
    case 'DECIDE_STEP_RENTS':
    case 'VALIDATE_INCENTIVE_COVENANT':
    case 'ESTIMATE_TENANT_FITOUT_COST':
    case 'REDACT_INCENTIVE_LETTER':
    case 'REDACT_LOI':
    case 'SELECT_DEAL_CONTACT':
    case 'SELECT_INCENTIVE_TEMPLATE':
    case 'SELECT_LOI_TEMPLATE':
    case 'RISK_ALERT_TENANT':
    case 'DATA_GAP_NO_LEASE_END_DATE':
    case 'STALE_DEAL':
      return 'DEALS';
    default:
      return 'OVERVIEW';
  }
};

interface UnitPageProps {
  unitId: string;
  className?: string;
  initialWorkspaceTab?: UnitWorkspaceTab;
  focusedActionType?: LeasingActionType;
  hasEntityRowAbove?: boolean;
}

function UnitOverviewTab({
  unitId,
  highlightedActionTypes,
}: {
  unitId: string;
  highlightedActionTypes: LeasingActionType[];
}) {
  const lang = useKernelStore((state) => state.language);
  const unit = useKernelStore((state) => state.inventory.Unit.registry[unitId]);

  const activeDeal = useKernelStore(state => 
    unit?.activeDealId ? state.inventory.Deal.registry[unit.activeDealId] : null
  );
  const brand = useKernelStore(state => 
    activeDeal?.brandId ? state.inventory.Brand.registry[activeDeal.brandId] : null
  );
  const tenantName = brand?.name || activeDeal?.tenantLegalName || null;
  const brandLogoUrl = brand?.logoUrl || null;
  // --------------------------------------------------

  const property = useKernelStore((state) => (unit?.propertyId ? state.inventory.Property.registry[unit.propertyId] : null));
  const { handleFloorPlanFileChange, floorPlanInputRef } = useFloorManagement(property?.id);

  const copy = useMemo(
    () =>
      lang === 'fr'
        ? {
            loading: 'Chargement...',
            subtitle: 'Fondation de l unite et qualite de donnees',
            statusOk: 'OK',
            statusMissing: 'A completer',
            surface: 'Surface',
            unitMapping: 'Position',
            mainPhoto: 'Photo principale',
            viewPhoto: 'Voir la photo',
            noPhoto: 'Aucune photo',
            validatedStatus: 'Statut',
            specifications: 'Specifications',
            type: 'Type',
            frontage: 'Facade',
            askingRent: 'Loyer demande',
            currentOccupancy: 'Occupation actuelle',
            unitVacant: "L unite est actuellement vacante.",
            description: 'Description',
            noDescription: "Aucune description detaillee pour cette unite.",
            associatedFloors: 'Etages associes',
            noFloorsAssociated: 'Aucun etage associe',
          }
        : {
            loading: 'Loading...',
            subtitle: 'Unit foundation and data quality',
            statusOk: 'OK',
            statusMissing: 'Missing',
            surface: 'Surface',
            unitMapping: 'Position',
            mainPhoto: 'Main photo',
            viewPhoto: 'View photo',
            noPhoto: 'No photo',
            validatedStatus: 'Status',
            specifications: 'Specifications',
            type: 'Type',
            frontage: 'Frontage',
            askingRent: 'Asking Rent',
            currentOccupancy: 'Current Occupancy',
            unitVacant: 'Unit is currently vacant.',
            description: 'Description',
            noDescription: 'No detailed description available for this unit.',
            associatedFloors: 'Associated Floors',
            noFloorsAssociated: 'No floors associated',
          },
    [lang]
  );

  const hasSurface = Boolean(
    (typeof unit?.areaSqm === 'number' && unit.areaSqm > 0) ||
      (typeof unit?.areaSqft === 'number' && unit.areaSqft > 0)
  );
  const hasPosition = Boolean(
    Array.isArray(unit?.positions) && unit.positions.some((position) => Boolean(position.geoJson))
  );
  const hasMainPhoto = Boolean(unit?.thumbnailFileId || unit?.thumbnailFileId);
  const hasStatus = Boolean(unit?.status);

  const flaggedSurface = highlightedActionTypes.includes('DATA_GAP_NO_UNIT_SURFACE');
  const flaggedPosition = highlightedActionTypes.includes('DATA_GAP_NO_UNIT_POSITION');
  const flaggedPhoto = highlightedActionTypes.includes('DATA_GAP_NO_MAIN_PHOTO');
  const flaggedStatus = highlightedActionTypes.includes('VALIDATE_UNIT_STATUS');

  if (!unit) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{copy.loading}</div>;
  }

  const statusTokenClass = (ok: boolean, highlighted: boolean) =>
    clsx(
      'rounded-full border px-2 py-0.5 text-[10px]',
      ok ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200' : 'border-rose-400/30 bg-rose-500/15 text-rose-200',
      highlighted && !ok && 'ring-1 ring-amber-300/60'
    );

  return (
    <div className="flex h-full flex-col bg-black">
      <input type="file" ref={floorPlanInputRef} className="hidden" accept="image/*" onChange={handleFloorPlanFileChange} />

      <div className="flex items-center justify-between border-b border-[#2A2A2A] bg-[#1D1D1D] px-4 py-3">
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-white/90">{unit.unitIdentifier}</div>
          <div className="text-[11px] text-white/45">{copy.subtitle}</div>
        </div>
      </div>

      <div className="border-b border-[#2A2A2A] bg-black px-4 py-2">
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-white/45">{copy.surface}</span>
          <span className={statusTokenClass(hasSurface, flaggedSurface)}>
            {hasSurface ? copy.statusOk : copy.statusMissing}
          </span>
          <span className="text-white/35">|</span>
          <span className="text-white/45">{copy.unitMapping}</span>
          <span className={statusTokenClass(hasPosition, flaggedPosition)}>
            {hasPosition ? copy.statusOk : copy.statusMissing}
          </span>
          <span className="text-white/35">|</span>
          <span className="text-white/45">{copy.mainPhoto}</span>
          <span className={statusTokenClass(hasMainPhoto, flaggedPhoto)}>
            {hasMainPhoto ? copy.statusOk : copy.statusMissing}
          </span>
          <span className="text-white/35">|</span>
          <span className="text-white/45">{copy.validatedStatus}</span>
          <span className={statusTokenClass(hasStatus, flaggedStatus)}>
            {hasStatus ? copy.statusOk : copy.statusMissing}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar">
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            <UnitDetailRow
              label={copy.mainPhoto}
              value={
                unit.thumbnailFileId ? (
                  <a
                    href={`/api/leasing/assets/${unit.thumbnailFileId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4AA3FF] underline-offset-2 hover:underline"
                  >
                    {copy.viewPhoto}
                  </a>
                ) : (
                  <span className="italic text-white/30">{copy.noPhoto}</span>
                )
              }
            />
            <UnitDetailRow label={copy.type} value={unit.type || '-'} />
            <UnitDetailRow
              label={copy.surface}
              value={
                unit.areaSqm
                  ? `${unit.areaSqm.toLocaleString()} m2`
                  : unit.areaSqft
                    ? `${(unit.areaSqft * 0.0929).toLocaleString()} m2`
                    : '-'
              }
              mono
            />
            <UnitDetailRow
              label={copy.frontage}
              value={unit.frontage ? `${unit.frontage} m` : '-'}
              mono
            />
            <UnitDetailRow
              label={copy.askingRent}
              value={
                unit.askingRent
                  ? `${unit.askingRent.toLocaleString()} ${unit.askingRentCurrency || 'EUR'}`
                  : '-'
              }
              mono
            />
            <UnitDetailRow
              label={copy.currentOccupancy}
              value={
                tenantName ? (
                  <div className="flex items-center gap-2">
                    <BrandMark logoUrl={brandLogoUrl} name={tenantName} size={20} />
                    <span className="text-white/90">{tenantName}</span>
                  </div>
                ) : (
                  <span className="italic text-white/30">{copy.unitVacant}</span>
                )
              }
            />
            <UnitDetailRow
              label={copy.description}
              value={
                unit.description ? (
                  <span className="whitespace-pre-wrap text-white/80">{unit.description}</span>
                ) : (
                  <span className="italic text-white/30">{copy.noDescription}</span>
                )
              }
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface UnitDetailRowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

function UnitDetailRow({ label, value, mono }: UnitDetailRowProps) {
  return (
    <tr className="border-b border-white/[0.06]">
      <th
        scope="row"
        className="w-[240px] bg-white/[0.015] px-4 py-2.5 text-left align-top text-[12px] font-normal text-white/45"
      >
        {label}
      </th>
      <td
        className={clsx(
          'px-4 py-2.5 align-top text-white/90',
          mono && 'font-mono text-[12px]'
        )}
      >
        {value}
      </td>
    </tr>
  );
}

const UNIT_LEADS_INITIAL_FILTERS = { sortBy: 'fitScore', sortDir: 'desc' } as const;

function UnitLeadsTab({ unitId }: { unitId: string }) {
  const lang = useKernelStore((state) => state.language);
  const unit = useKernelStore((state) => state.inventory.Unit.registry[unitId]);
  const leadsLockedFilters = useMemo(() => ({ propertyId: unit?.propertyId }), [unit?.propertyId]);

  const copy = useMemo(
    () =>
      lang === 'fr'
        ? {
            loading: 'Chargement...',
            title: 'Leads',
            subtitle: 'Leads lies a cette unite (avec unit fit)',
            launchSelection: 'Lancer unit-fit sur selection',
            noEligibleLeads: 'Aucun lead IDENTIFIED ou SUCCESS disponible pour lancer le unit-fit.',
          }
        : {
            loading: 'Loading...',
            title: 'Leads',
            subtitle: 'Leads linked to this unit (unit-fit scoped)',
            launchSelection: 'Run unit-fit on selection',
            noEligibleLeads: 'No IDENTIFIED or SUCCESS leads are available for this unit-fit run.',
          },
    [lang]
  );

  const renderUnitLeadsToolbarHeader = useCallback(
    () => (
      <div className="min-w-0 text-right">
        <div className="text-[12px] font-medium text-white/90">{copy.title}</div>
        <div className="text-[11px] text-white/45">{copy.subtitle}</div>
      </div>
    ),
    [copy.subtitle, copy.title]
  );

  if (!unit) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{copy.loading}</div>;
  }

  return (
    <div className="h-full">
      <LeadsTable
        initialFilters={UNIT_LEADS_INITIAL_FILTERS}
        lockedFilters={leadsLockedFilters}
        propertyLocked
        leadLockedUnitId={unitId}
        assetLedgerTitle={lang === 'fr' ? 'Leads de l unite' : 'Unit leads'}
        assetLedgerBreadcrumb={lang === 'fr' ? 'Unite / Leads' : 'Unit / Leads'}
        renderCustomToolbarHeader={renderUnitLeadsToolbarHeader}
      />
    </div>
  );
}

function UnitDealsTab({ unitId }: { unitId: string }) {
  const lang = useKernelStore((state) => state.language);
  const unit = useKernelStore((state) => state.inventory.Unit.registry[unitId]);
  const dealsLockedFilters = useMemo(() => ({ unitId }), [unitId]);

  const copy = useMemo(
    () =>
      lang === 'fr'
        ? {
            loading: 'Chargement...',
            title: 'Deals',
            subtitle: 'Tous les deals lies a cette unite',
            count: (count: number) => `${count} deal(s)`,
          }
        : {
            loading: 'Loading...',
            title: 'Deals',
            subtitle: 'All deals linked to this unit',
            count: (count: number) => `${count} deal(s)`,
          },
    [lang]
  );

  const renderUnitDealsToolbarHeader = useCallback(
    () => (
      <div className="min-w-0 text-right">
        <div className="text-[12px] font-medium text-white/90">{copy.title}</div>
        <div className="text-[11px] text-white/45">{copy.subtitle}</div>
        <div className="text-[11px] text-white/55">
          {typeof unit?.dealCount === 'number' ? copy.count(unit.dealCount) : '...'}
        </div>
      </div>
    ),
    [copy, unit?.dealCount]
  );

  if (!unit) {
    return <div className="p-8 text-center text-white/30 animate-pulse">{copy.loading}</div>;
  }

  return (
    <div className="h-full">
      <DealsTable
        lockedFilters={dealsLockedFilters}
        assetLedgerTitle={lang === 'fr' ? 'Deals de l unite' : 'Unit deals'}
        assetLedgerBreadcrumb={lang === 'fr' ? 'Unite / Deals' : 'Unit / Deals'}
        showDefaultToolbarSelectors={false}
        renderCustomToolbarHeader={renderUnitDealsToolbarHeader}
      />
    </div>
  );
}

const UNIT_PAGE_TABS = new Set<UnitWorkspaceTab>(['OVERVIEW', 'LEADS', 'DEALS']);
const LEASING_ACTION_TYPE_SET = new Set<LeasingActionType>(LEASING_ACTION_TYPES);

const isLeasingActionType = (value: unknown): value is LeasingActionType =>
  typeof value === 'string' && LEASING_ACTION_TYPE_SET.has(value as LeasingActionType);

export const UnitPage = ({
  unitId,
  className,
  initialWorkspaceTab,
  focusedActionType,
  hasEntityRowAbove = false,
}: UnitPageProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const activeSessionSubject = useKernelStore((state) =>
    activeChatSessionId ? state.inventory.ChatSession.registry[activeChatSessionId]?.activeSubject ?? null : null
  );
  const unit = useKernelStore(
    (state) => (state.inventory.Unit.registry[unitId] as UnitInventoryEntityState | undefined) ?? null
  );

  const effectiveFocusedActionType = useMemo<LeasingActionType | undefined>(() => {
    if (activeSessionSubject?.entityType === 'Unit' && activeSessionSubject.entityId === unitId) {
      return activeSessionSubject.focusActionType;
    }
    return focusedActionType;
  }, [activeSessionSubject, unitId, focusedActionType]);

  const fallbackWorkspaceTab = useMemo<UnitWorkspaceTab>(() => {
    if (initialWorkspaceTab) return initialWorkspaceTab;
    return resolveUnitWorkspaceTabForAction(effectiveFocusedActionType);
  }, [initialWorkspaceTab, effectiveFocusedActionType]);

  const activeTabCandidate = unit?.activeTab ?? unit?.runtime?.activeTab;
  const activeTab =
    typeof activeTabCandidate === 'string' && UNIT_PAGE_TABS.has(activeTabCandidate as UnitWorkspaceTab)
      ? (activeTabCandidate as UnitWorkspaceTab)
      : fallbackWorkspaceTab;

  const patchKernelState = useCallback(
    (patch: Partial<Pick<UnitRuntimeState, 'activeTab' | 'lastFocusedActionType' | 'entityActions'>>) => {
      if (!kernel.current) return;
      kernel.current.patchEntityRuntimeState('Unit', unitId, patch);
    },
    [kernel, unitId]
  );

  useEffect(() => {
    if (!kernel.current) return;
    if (typeof activeTabCandidate === 'string' && UNIT_PAGE_TABS.has(activeTabCandidate as UnitWorkspaceTab)) return;
    kernel.current.patchEntityRuntimeState('Unit', unitId, {
      activeTab: fallbackWorkspaceTab,
      lastFocusedActionType: effectiveFocusedActionType,
    });
  }, [kernel, unitId, activeTabCandidate, fallbackWorkspaceTab, effectiveFocusedActionType]);

  useEffect(() => {
    if (!effectiveFocusedActionType) return;
    const lastFocusedActionType = unit?.lastFocusedActionType ?? unit?.runtime?.lastFocusedActionType;
    const alreadyApplied = lastFocusedActionType === effectiveFocusedActionType;
    if (alreadyApplied) return;
    patchKernelState({
      activeTab: resolveUnitWorkspaceTabForAction(effectiveFocusedActionType),
      lastFocusedActionType: effectiveFocusedActionType,
    });
  }, [effectiveFocusedActionType, unit?.lastFocusedActionType, unit?.runtime?.lastFocusedActionType, patchKernelState]);

  useEffect(() => {
    if (!kernel.current || !unitId) return;
    if (!unit || unit.floorIds === undefined || typeof unit.dealCount !== 'number') {
      void kernel.current.syncEntity('Unit', unitId);
    }
  }, [kernel, unitId, unit?.id, unit?.floorIds, unit?.dealCount, unit]);

  const actionChoices = useMemo(() => {
    const source = unit?.entityActions ?? unit?.runtime?.entityActions ?? [];
    if (!Array.isArray(source)) return [];
    return source.reduce<Array<{ type: LeasingActionType; entityType?: string; entityId?: string }>>((acc, candidate) => {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return acc;
      const record = candidate as Record<string, unknown>;
      if (!isLeasingActionType(record.type)) return acc;
      acc.push({
        type: record.type,
        entityType: typeof record.entityType === 'string' ? record.entityType : undefined,
        entityId: typeof record.entityId === 'string' ? record.entityId : undefined,
      });
      return acc;
    }, []);
  }, [unit?.entityActions, unit?.runtime?.entityActions]);

  const highlightedTabs = useMemo(() => {
    const tabs = new Set<UnitWorkspaceTab>();
    for (const action of actionChoices) {
      tabs.add(resolveUnitWorkspaceTabForAction(action.type));
    }
    return tabs;
  }, [actionChoices]);

  const highlightedActionTypes = useMemo(
    () => Array.from(new Set(actionChoices.map((action) => action.type))),
    [actionChoices]
  );

  const tabs = useMemo(
    () => [
      { id: 'OVERVIEW' as const, label: lang === 'fr' ? 'Fondation' : 'Overview', hasPendingIndicator: highlightedTabs.has('OVERVIEW') },
      { id: 'LEADS' as const, label: 'Leads', hasPendingIndicator: highlightedTabs.has('LEADS') },
      { id: 'DEALS' as const, label: 'Deals', hasPendingIndicator: highlightedTabs.has('DEALS') },
    ],
    [lang, highlightedTabs]
  );

  const onTabChange = useCallback(
    (nextTab: UnitWorkspaceTab) => {
      patchKernelState({ activeTab: nextTab });
    },
    [patchKernelState]
  );

  return (
    <div className={clsx('flex min-h-0 flex-col h-full bg-[#141414] text-white', className)}>
      <EntityPageTabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} sticky />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {activeTab === 'OVERVIEW' && (
          <UnitOverviewTab unitId={unitId} highlightedActionTypes={highlightedActionTypes} />
        )}
        {activeTab === 'LEADS' && (
          <UnitLeadsTab unitId={unitId} />
        )}
        {activeTab === 'DEALS' && (
          <UnitDealsTab unitId={unitId} />
        )}
      </div>
    </div>
  );
};



